// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { Editor as DraftEditor } from 'react-draft-wysiwyg';
import { EditorState } from 'draft-js';
import { Editor } from './Editor';
import { TextEditorFooter } from './TextEditorFooter';
import {
  StyledEditorSide,
  StyledEditorWrapper,
  StyledScreenWrapper,
  StyledTextEditor,
  StyledTextEditorContent,
  StyledTextEditorContentInner,
} from '../../Styled';
import { LaterExemplar, TextTypes } from '../../../types';
import { COLORS, EXEMPLAR_MODES } from '../../../constants';
import { getSections } from '../../../utils';
import { getExemplarLinesLimits, getTextEditorProps } from '../../../utils/textEditor/common';
import {
  getLaterEditorStateFromSide,
  getLaterTextEditorToolbar,
  getNewLaterFromSides,
} from '../../../utils/textEditor/laterTextEditor';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

interface ILaterTextEditor {
  exemplar: LaterExemplar;
  defaultExemplar: LaterExemplar;
  mainColor: string;
  page: string;
  updateEditedExemplar: (editedExemplar: LaterExemplar) => void;
  onSave: (exemplar: LaterExemplar) => void;
}

export const LaterTextEditor: React.FC<ILaterTextEditor> = (props): JSX.Element => {
  const { exemplar, defaultExemplar, mainColor, page, updateEditedExemplar, onSave } = props;

  const initLeftEditorState = useMemo(
    () => getLaterEditorStateFromSide(exemplar.content.left),
    [exemplar.content.left]
  );
  const initRightEditorState = useMemo(
    () => getLaterEditorStateFromSide(exemplar.content.right),
    [exemplar.content.right]
  );
  const exemplarLinesLimits = useMemo(() => getExemplarLinesLimits(defaultExemplar.content), [defaultExemplar]);

  const [leftEditorState, setLeftEditorState] = useState(initLeftEditorState);
  const [rightEditorState, setRightEditorState] = useState(initRightEditorState);
  const [isFocusedEditor, setIsFocusedEditor] = useState(false);

  const isProcedure = exemplar?.text_type === TextTypes.procedure;
  const primaryColor = isProcedure && page === EXEMPLAR_MODES.textPresentation ? COLORS.venetianRed : COLORS.black;

  const updateLeftSide = (id: string) => {
    return (state: EditorState) => {
      setLeftEditorState({
        ...leftEditorState,
        [id]: {
          label: leftEditorState[id].label,
          value: state,
          meta: leftEditorState[id].meta,
        },
      });
    };
  };

  const updateRightSide = (id: string) => {
    return (state: EditorState) => {
      setRightEditorState({
        ...rightEditorState,
        [id]: {
          label: rightEditorState[id].label,
          value: state,
          meta: rightEditorState[id].meta,
        },
      });
    };
  };

  const handleFocus = () => {
    setIsFocusedEditor(true);
  };

  const handleBlur = () => {
    const updatedExemplar = getNewLaterFromSides(exemplar, leftEditorState, rightEditorState);
    updateEditedExemplar(updatedExemplar);
    setIsFocusedEditor(false);
  };

  const save = () => {
    onSave(getNewLaterFromSides(exemplar, leftEditorState, rightEditorState));
  };

  const renderTextEditor = () => {
    return (
      <StyledEditorWrapper>
        <StyledEditorSide>
          {getSections(exemplar.content.left).map((section, index) => {
            const isTitle = section.style?.includes('heading');

            return (
              <Editor
                key={section.id}
                id={section.id}
                toolbarConfig={getLaterTextEditorToolbar({
                  showHeading: !isTitle && isProcedure,
                  showList: !isTitle && isProcedure,
                })}
                editorState={(leftEditorState[section.id] || {}).value}
                headingColor={primaryColor}
                listColor={primaryColor}
                blocksLimit={exemplarLinesLimits[`left-${index}`]}
                isLimited={isTitle || !isProcedure}
                isTitle={isTitle}
                onEditorStateChange={updateLeftSide(section.id)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            );
          })}
        </StyledEditorSide>
        <StyledEditorSide>
          <StyledTextEditor data-testid="later-text-editor" stage={exemplar.stage} isHidden={true}>
            <DraftEditor
              {...getTextEditorProps(false, true)}
              toolbarHidden={isFocusedEditor}
              toolbar={getLaterTextEditorToolbar()}
              key={`${exemplar.content.left[0].id}-hidden`}
              defaultEditorState={(leftEditorState[exemplar.content.left[0].id as string] || {}).value}
            />
          </StyledTextEditor>
          {getSections(exemplar.content.right).map((section, index) => {
            return (
              <Editor
                key={section.id}
                id={section.id}
                toolbarConfig={getLaterTextEditorToolbar({ showHeading: isProcedure, showList: isProcedure })}
                editorState={(rightEditorState[section.id] || {}).value}
                headingColor={primaryColor}
                listColor={primaryColor}
                blocksLimit={exemplarLinesLimits[`right-${index}`]}
                isLimited={!isProcedure}
                isRight={true}
                onEditorStateChange={updateRightSide(section.id)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            );
          })}
        </StyledEditorSide>
      </StyledEditorWrapper>
    );
  };

  return (
    <StyledTextEditorContent>
      <StyledTextEditorContentInner>
        <StyledScreenWrapper>{renderTextEditor()}</StyledScreenWrapper>
        <TextEditorFooter mainColor={mainColor} onSave={save} />
      </StyledTextEditorContentInner>
    </StyledTextEditorContent>
  );
};
